import type { ClientRecurringOrdersResponse, PortfolioOrderStatus } from "@repo/shared/types";
import { TriangleAlert } from "lucide-react";
import { useState } from "react";
import { OrderFlowGroup } from "@/components/orders/order-flow-group";
import { OrderListToolbar } from "@/components/orders/order-list-toolbar";
import { OrderStatusBadge } from "@/components/orders/order-status-badge";
import { EmptyState } from "@/components/ui/empty-state";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { cn } from "@/lib/utils";
import { useFrequencySuffix } from "./frequency-suffix";
import { formatWholeAmount, getOrderProductName } from "./order-product-name";
import { T } from "./recurring-orders-section.i18n";

type RecurringOrdersSectionProps = {
  data: ClientRecurringOrdersResponse;
  onMutate: () => void;
};

export function RecurringOrdersSection({ data, onMutate }: RecurringOrdersSectionProps) {
  const { formatDate, formatNumber } = useRegionalFormat();
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const frequencySuffix = useFrequencySuffix();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [expandedFlows, setExpandedFlows] = useState<Set<string>>(new Set());

  const toggleFlow = (id: string) => {
    setExpandedFlows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Filter orders and remove empty groups
  const filteredFlows = data.flowInstances
    .map((fi) => ({
      ...fi,
      agreements: fi.agreements
        .map((a) => ({
          ...a,
          orders:
            statusFilter === "all" ? a.orders : a.orders.filter((o) => o.status === statusFilter),
        }))
        .filter((a) => a.orders.length > 0),
    }))
    .filter((fi) => fi.agreements.length > 0);

  const totalOrders = data.flowInstances.reduce(
    (sum, fi) => sum + fi.agreements.reduce((s, a) => s + a.orders.length, 0),
    0,
  );

  if (totalOrders === 0) {
    return <EmptyState>{t.empty}</EmptyState>;
  }

  return (
    <div className="space-y-4">
      <OrderListToolbar
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        onRefresh={onMutate}
        language={userLanguage}
      />

      {filteredFlows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">{t.noOrdersFilter}</p>
      ) : (
        <div className="space-y-3">
          {filteredFlows.map((fi) => {
            const count = fi.agreements.reduce((s, a) => s + a.orders.length, 0);
            return (
              <OrderFlowGroup
                key={fi.flowInstanceId}
                flowName={fi.flowName}
                completedAt={fi.completedAt}
                summary={`${count} ${count !== 1 ? t.orders : t.order}`}
                expanded={expandedFlows.has(fi.flowInstanceId)}
                onToggle={() => toggleFlow(fi.flowInstanceId)}
                language={userLanguage}
              >
                {fi.agreements.map((agreement) => (
                  <div key={agreement.savingsPlanId ?? "__deleted__"} className="space-y-2">
                    {/* Agreement heading */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4
                        className={cn("text-sm font-medium", !agreement.savingsPlanId && "italic")}
                      >
                        {agreement.planName ?? t.deletedAgreement}
                      </h4>
                      {agreement.planStatus === "pending" && (
                        <StatusBadge status="pending">{t.pendingPlan}</StatusBadge>
                      )}
                      {agreement.planStatus === "active" && (
                        <StatusBadge status="active">{t.activePlan}</StatusBadge>
                      )}
                      {agreement.goalName && (
                        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                          {agreement.goalName}
                        </span>
                      )}
                    </div>

                    <Table variant="data" surface={false}>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t.product}</TableHead>
                          <TableHead className="w-[140px]">{t.isin}</TableHead>
                          <TableHead className="text-right w-[150px]">{t.amount}</TableHead>
                          <TableHead className="w-[110px]">{t.start}</TableHead>
                          <TableHead className="w-[160px]">{t.account}</TableHead>
                          <TableHead className="w-[110px]">{t.status}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {agreement.orders.map((order) => (
                          <TableRow key={order.id}>
                            <TableCell className="font-medium">
                              {order.name ||
                                getOrderProductName(order.productName, userLanguage, order.isin)}
                            </TableCell>
                            <TableCell className="font-mono text-xs text-muted-foreground">
                              {order.isin ?? "—"}
                            </TableCell>
                            <TableCell className="text-right">
                              {order.type === "amount"
                                ? `${formatWholeAmount(order.size, formatNumber)}${order.currency ? ` ${order.currency}` : ""}`
                                : `${order.size} ${t.units}`}
                              <span className="text-xs text-muted-foreground">
                                {frequencySuffix(order.frequency)}
                              </span>
                            </TableCell>
                            <TableCell>
                              {order.startDate ? formatDate(order.startDate) : "—"}
                            </TableCell>
                            {/* accountName resolves the soft accountId server-side —
                                a dash when it dangles. */}
                            <TableCell className="truncate">{order.accountName ?? "—"}</TableCell>
                            <TableCell>
                              <span className="inline-flex items-center gap-1.5">
                                <OrderStatusBadge
                                  status={order.status as PortfolioOrderStatus}
                                  language={userLanguage}
                                />
                                {order.statusReason &&
                                  ["failed", "cancelled", "partially_executed"].includes(
                                    order.status,
                                  ) && (
                                    <TriangleAlert
                                      className="h-3.5 w-3.5 shrink-0 text-warning"
                                      aria-label={order.statusReason}
                                    >
                                      <title>{order.statusReason}</title>
                                    </TriangleAlert>
                                  )}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ))}
              </OrderFlowGroup>
            );
          })}
        </div>
      )}
    </div>
  );
}
